import * as React from 'react';
import {
  HeadingText,
  Icon,
  ParagraphText,
  StackLayout,
  StatusBadge,
  styled,
  UtilityText,
  useClickableCard,
  Image,
  type GDSStatus,
} from '@leagueplatform/genesis-core';
import { Thermobar } from '@leagueplatform/genesis-core-alpha';
import { HtmlToReact } from '@leagueplatform/web-common';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';

import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryDriverAction,
} from '../../types/masonry-driver-node-actions';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

type Measurement = {
  value: string;
  unit?: string;
};

export type RiskLabel = {
  label: string;
  style: GDSStatus;
};

export type ThermobarTickmark = {
  value: number;
  label?: string;
};

export type ThermobarIndicator = {
  value: number;
  style?: GDSStatus;
};

export type ThermobarConfig = {
  min?: number;
  max?: number;
  tickmarks?: ThermobarTickmark[];
  indicator: ThermobarIndicator;
  accessibilityLabel?: string;
};

export type MasonryMetricCardNodeProperties = {
  heading: string;
  measurements?: Measurement[];
  body?: string;
  timestamp?: string;
  image?: string;
  imageAltText?: string;
  riskLabel?: RiskLabel;
  thermobar?: ThermobarConfig;
};

export type MasonryMetricCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryMetricCardNode = MasonryEngineNode<
  'metricCard',
  MasonryMetricCardNodeProperties,
  MasonryMetricCardNodeActions
>;

export type MasonryMetricCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryMetricCardNode>;

const StyledImage = styled(Image, {
  objectFit: 'cover',
  contentPosition: 'center',
  width: '$three',
  height: '$three',
});

function getValidatedThermobar(
  thermobar?: ThermobarConfig,
): ThermobarConfig | undefined {
  if (!thermobar) {
    return undefined;
  }

  const min = thermobar.min ?? 0;
  const max = thermobar.max ?? 100;

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return undefined;
  }

  const hasInvalidTickmark = thermobar.tickmarks?.some(
    ({ value }) => !Number.isFinite(value) || value < min || value > max,
  );

  if (hasInvalidTickmark) {
    return undefined;
  }

  if (
    !Number.isFinite(thermobar.indicator.value) ||
    thermobar.indicator.value < min ||
    thermobar.indicator.value > max
  ) {
    return undefined;
  }

  return {
    ...thermobar,
    min,
    max,
  };
}

export function MasonryMetricCardRenderer({
  heading,
  measurements,
  body,
  timestamp,
  image,
  imageAltText = '',
  riskLabel,
  thermobar,
  onClick,
  onDismiss,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
}: MasonryMetricCardRendererProps) {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);
  // using double ! operator to get explicit booleans, rather than just truthy/falsy values
  const displayMeasurements = !!measurements?.length;
  // display body if: no `measurements` exist AND `body` exists
  const displayBody = !displayMeasurements && !!body;
  const invokeAction = useInvokeAction();
  const validatedThermobar = getValidatedThermobar(thermobar);

  return (
    <ConditionalLinkCardRenderer
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      hasLink={Boolean((onClick as MasonryDeeplinkAction)?.payload.url)}
      onDismiss={onDismiss}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        horizontalAlignment="spaceBetween"
        spacing="$threeQuarters"
        css={{
          padding: `${onDismiss ? '0 $one $one ' : '$one'}`,
          ...(validatedThermobar?.tickmarks?.some((t) => t.label) && {
            paddingBlockEnd: '$two',
          }),
          flexWrap: 'wrap',
        }}
      >
        {image && <StyledImage loading="lazy" src={image} alt={imageAltText} />}
        <StackLayout
          css={{
            width: '100%',
          }}
          orientation="horizontal"
          horizontalAlignment="spaceBetween"
        >
          <StackLayout
            orientation={{
              '@initial': 'horizontal',
              '@mobile': 'vertical',
              '@mobileLandscape': 'vertical',
              '@tablet': 'vertical',
            }}
            spacing="$one"
            verticalAlignment="center"
            css={{ flexWrap: 'wrap' }}
          >
            <StackLayout
              spacing="$quarter"
              verticalAlignment="spaceBetween"
              css={{
                flex: '0 0 20%',
                maxWidth: '200px',
                '@mobile': { flex: 'initial', maxWidth: 'none' },
                '@mobileLandscape': { flex: 'initial', maxWidth: 'none' },
                '@tablet': { flex: 'initial', maxWidth: 'none' },
              }}
            >
              <HeadingText
                size="sm"
                level={!body ? 'display' : '3'}
                color="$onSurfaceTextPrimary"
              >
                <ConditionalLinkRenderer
                  disableDefaultOnClick
                  ref={primaryActionRef}
                  onClick={(event: React.MouseEvent) => {
                    if (onClick) {
                      invokeAction({
                        event,
                        ...onClick,
                      });
                    }
                  }}
                  url={(onClick as MasonryDeeplinkAction)?.payload.url}
                >
                  {heading}
                </ConditionalLinkRenderer>
              </HeadingText>
              {displayMeasurements ? (
                <StackLayout orientation="horizontal">
                  <ParagraphText
                    data-testid="measurement"
                    css={{
                      '&&': {
                        marginBlockEnd: '$none',
                      },
                    }}
                  >
                    {measurements.map(({ value, unit }) => (
                      <React.Fragment key={`${value}${unit}`}>
                        <UtilityText
                          css={{
                            paddingInlineEnd: '$quarter',
                            typography: '$headingThree',
                          }}
                        >
                          {value}
                        </UtilityText>
                        {unit && (
                          <UtilityText
                            css={{
                              typography: '$label',
                              color: '$onSurfaceTextSubdued',
                              paddingInlineEnd: '$quarter',
                            }}
                          >
                            {unit}{' '}
                          </UtilityText>
                        )}
                      </React.Fragment>
                    ))}
                  </ParagraphText>
                </StackLayout>
              ) : null}
              {displayBody ? (
                <UtilityText
                  css={{
                    typography: '$caption',
                    color: '$onSurfaceTextSubdued',
                  }}
                >
                  <HtmlToReact htmlString={body} />
                </UtilityText>
              ) : null}
            </StackLayout>
            <StackLayout
              orientation={{
                '@initial': 'horizontal',
                '@mobile': 'vertical',
                '@mobileLandscape': 'vertical',
                '@tablet': 'vertical',
              }}
              verticalAlignment="center"
              spacing={{
                '@initial': '$oneAndHalf',
                '@mobile': '$half',
                '@mobileLandscape': '$half',
                '@tablet': '$half',
              }}
              css={{
                '@mobile': { width: '100%' },
                '@mobileLandscape': { width: '100%' },
                '@tablet': { width: '100%' },
              }}
            >
              {riskLabel && (
                <StatusBadge
                  label={riskLabel.label}
                  status={riskLabel.style}
                  showLight={false}
                  css={{ whiteSpace: 'nowrap' }}
                />
              )}

              {validatedThermobar && (
                <Thermobar
                  value={validatedThermobar.indicator.value}
                  min={validatedThermobar.min}
                  max={validatedThermobar.max}
                  tickmarks={validatedThermobar.tickmarks?.map((t) => ({
                    value: t.value,
                    label: t.label,
                  }))}
                  indicatorStyle={validatedThermobar.indicator.style}
                  accessibilityLabel={
                    validatedThermobar.accessibilityLabel ?? ''
                  }
                />
              )}
            </StackLayout>
          </StackLayout>

          {timestamp && (
            <ParagraphText
              css={{
                typography: '$label',
                color: '$onSurfaceTextSubdued',
              }}
            >
              {timestamp}
            </ParagraphText>
          )}
        </StackLayout>
        {(onClick as MasonryDeeplinkAction)?.payload.url && (
          <Icon
            size={24}
            icon="interfaceChevronForward"
            tint="$onSurfaceIconPrimary"
          />
        )}
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
}
